// src/routes/AppRoutes.jsx
import { lazy, Suspense, useEffect } from "react";
import { FileSearch, FileText, Ruler, Code2, FlaskConical, Rocket } from "lucide-react";
import api from "../api/client";
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
} from "react-router-dom";

// RDS UI pages (legacy — Phase 5 of the studio redesign deletes this folder)
import RdsRequirementStudio from "../rds_ui/pages/RequirementStudio";
import RdsDesignStudio from "../rds_ui/pages/DesignStudio";
import RdsDesignProjectDetail from "../rds_ui/pages/DesignProjectDetail";
import RdsSOWDetail from "../rds_ui/pages/SOWDetail";
import RdsGenerationProgress from "../rds_ui/pages/GenerationProgress";
import RdsGenerationResults from "../rds_ui/pages/GenerationResults";
import RdsPromptEditor from "../rds_ui/pages/PromptEditor";

// Requirements & Design Studio (redesign) — src/studio/, independent of
// rds_ui/ so that folder can be deleted in one commit at Phase 5.
import StudioHome from "../studio/pages/StudioHome";
import StudioProjectLayout from "../studio/pages/StudioProjectLayout";
import SourcesStage from "../studio/pages/SourcesStage";
import DocumentStagePage from "../studio/pages/DocumentStagePage";

import Dashboard from "../pages/Dashboard";
import UsersPage from "../admin/pages/UsersPage";
import RolesPage from "../admin/pages/RolesPage";
import PermissionsPage from "../admin/pages/PermissionsPage";
import SubscriptionsPage from "../pages/SubscriptionsPage";
import ConnectorsPage from "../connectors/pages/ConnectorsPage";
// Real, backend-integrated Accounts/Projects (account:manage permission,
// Conductor engagements). The older duplicate top-level pages
// (pages/AccountsPage.jsx as "AccountTierPage", pages/EngagementsPage.jsx)
// were removed — this is the one surface now; only the detail pages
// (role/team management) remain standalone.
import AccountsPage from "../accounts/pages/AccountsPage";
import ProjectsPage from "../accounts/pages/ProjectsPage";
import AccountDetailPage from "../accounts/pages/AccountDetailPage";
import EngagementDetailPage from "../accounts/pages/EngagementDetailPage";

import AcceleratorsPage from "../pages/accelerators/AcceleratorsPage.jsx";

import RagCollectionDetailPage from "../pages/RagCollectionDetailPage.jsx";
import RagCollectionsPage from "../pages/RagCollectionsPage.jsx";
import RagSearchPage from "../pages/RagSearchPage.jsx";
import RagAnalyticsPage from "../pages/RagAnalyticsPage.jsx";

import LoginPage from "../pages/LoginPage";
import SsoCallback from "../pages/SsoCallback";
import Unauthorized from "../pages/Unauthorized";
import NotFound from "../pages/NotFound";

import ProtectedOutlet from "./ProtectedOutlet";
import ProtectedRoute from "./ProtectedRoute";
import ModuleRouteGuard from "./ModuleRouteGuard";
import RagJobsPage from "../pages/RagJobsPage.jsx";
import RagClientScriptsPage from "../pages/RagClientScriptsPage.jsx";
import DataSourcesPage from "../GDB/pages/DataSourcesPage.jsx";
import DatasetsPage from "../GDB/pages/DatasetBuilderPage.jsx";
import DashboardEditPage from "../GDB/pages/DashboardEditPage.jsx";
import MyDashboardsPage from "../GDB/pages/MyDashboardsPage";
import CSVDatasetsPage from "../GDB/pages/CSVDatasetsPage.jsx";
import CSVDatasetUpload from "../GDB/components/CSVDatasetUpload.jsx";

import RulesConfigPage from "../pages/rules/RulesConfigPage.jsx";
import ReportDetailPage from "../pages/ReportDetailPage.jsx";
import ReportsPage from "../pages/ReportsListPage.jsx";
import SowDraftPage from "../pages/sow-draft/SowDraftPage.jsx";
import SowTemplatePage from "../pages/sow-template/SowTemplatePage.jsx";
import { ErrorBoundary, Spinner } from "../shared/ui";
import SowAnalysisPage from "../pages/sow-analysis/SowAnalysisPage.jsx";
// Code-split, per frontend/CLAUDE.md's ask that new heavy pages be
// lazy-loaded rather than bundled into the main chunk. Note this splits
// only these pages' own code (and reportFormat) — it does NOT keep
// recharts out of the main bundle, because RagAnalyticsPage and GDB's
// ChartWidget already import it eagerly. Making recharts itself
// lazy would mean converting those two as well.
const SowUsageReportPage = lazy(() => import("../usage/pages/SowUsageReportPage.jsx"));
const SowLogReportPage = lazy(() => import("../usage/pages/SowLogReportPage.jsx"));
// ✅ IT module imports

import ITNewRequest from "../IT/pages/ITNewRequest";
import ITMyRequests from "../IT/pages/ITMyRequests";
import ITApprovals from "../IT/pages/ITApprovals";
import ITRequestDetail from "../IT/pages/ITRequestDetail";
import ITChatbot from "../IT/pages/ITChatbot";

import XenonAiPage from "../xenonai/pages/XenonAiPage.jsx";

// ── Orion GoLive module ───────────────────────────────────────────────────
import GoLiveLayout from "../golive/GoLiveLayout.jsx";
import GoLiveHomePage from "../golive/pages/GoLiveHomePage.jsx";
import RunListPage from "../golive/pages/RunListPage.jsx";
import NewDeploymentPage from "../golive/pages/NewDeploymentPage.jsx";
import RunDetailPage from "../golive/pages/RunDetailPage.jsx";

// Codebase module — Add/Edit are modals on RepoListPage/RepoOverviewPage now,
// not separate routes; connection registration lives at /connectors.
import RepoListPage from "../codebase/pages/RepoListPage.jsx";
import RepoOverviewPage from "../codebase/pages/RepoOverviewPage.jsx";
import ScoringRulesPage from "../codebase/pages/ScoringRulesPage.jsx";

import MCPChat from "../mcp/pages/MCPChat.jsx";

// Requirements & Design Studio
import CompliancePage from "../pages/compliance/CompliancePage.jsx";

// Wraps RDS pages with font-size=12px (matching app baseline) and a scoped CSS reset
// that replicates Tailwind preflight for RDS elements without affecting the rest of the app.
function RdsFontReset() {
  useEffect(() => {
    const prev = document.documentElement.style.fontSize;
    document.documentElement.style.fontSize = "12px";
    return () => {
      document.documentElement.style.fontSize = prev;
    };
  }, []);
  return (
    <div className="rds-scope">
      <Outlet />
    </div>
  );
}

import AdaiPage from "../adai/pages/AdaiPage.jsx";
import ReviewGenPage from "../pages/reviewgen/ReviewGenPage.jsx";
import Faq from "../pages/FAQ.jsx";

// Restyled to match the approved Jade Orion design system (shell.css /
// index.css --jade-* tokens: Montserrat display type, Open Sans body, the
// jade-blue/gold palette) instead of the old ad-hoc inline styles. Rebuilt
// as a proper hero + step-card layout (rather than plain text) per the
// "make this UI very impressive" pass.
function DashboardHome() {
  const steps = [
    { icon: FileSearch, label: "RFP response", note: "Capture intent" },
    { icon: FileText, label: "SOW analysis & generation", note: "Scope it" },
    { icon: Ruler, label: "Requirements & design", note: "Blueprint it" },
    { icon: Code2, label: "Code analysis", note: "Build with confidence" },
    { icon: FlaskConical, label: "Test generation", note: "Prove it works" },
    { icon: Rocket, label: "Deployment & insights", note: "Ship & learn" },
  ];

  return (
    <div className="orion-home">
      <div className="orion-home-hero">
        <div className="orion-home-hero-glow" aria-hidden="true" />
        <div className="orion-home-eyebrow">Delivery Intelligence Platform</div>
        <h2 className="orion-home-title">Welcome to Jade Orion</h2>
        <p className="orion-home-body">
          Jade Orion is an enterprise-grade AI platform that unifies the entire
          software delivery lifecycle into one intelligent, audit-ready flow —
          a &ldquo;delivery brain&rdquo; that sits above your tools and
          technologies, orchestrating work across presales, architecture,
          development, QA, DevOps, and compliance.
        </p>
      </div>

      <div className="orion-home-steps">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div className="orion-home-step" key={s.label} style={{ "--i": i }}>
              <span className="orion-home-step-ico"><Icon size={18} /></span>
              <div className="orion-home-step-meta">
                <div className="orion-home-step-label">{s.label}</div>
                <div className="orion-home-step-note">{s.note}</div>
              </div>
              <span className="orion-home-step-n">{i + 1}</span>
            </div>
          );
        })}
      </div>

      <p className="orion-home-hint">
        AI guides each step and generates artefacts that support ISO and
        CMMI-aligned processes. Use the left menu to navigate between phases.
      </p>
    </div>
  );
}

export default function AppRoutes() {
  useEffect(() => {
    // Refresh Google ID token in the background every 45 minutes
    const interval = setInterval(() => {
      api.get("/auth/google/refresh").catch(console.error);
    }, 45 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/sso/callback" element={<SsoCallback />} />
        <Route path="/unauthorized" element={<Unauthorized />} />

        {/* Protected shell */}
        <Route element={<ProtectedOutlet />}>
          <Route path="/" element={<Dashboard />}>
            {/* Landing inside dashboard */}
            <Route index element={<DashboardHome />} />

            {/* Admin */}
            <Route
              path="users"
              element={
                <ProtectedRoute requiredPermissions={["admin:manage_users"]}>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="roles"
              element={
                <ProtectedRoute requiredPermissions={["identity:roles:view"]}>
                  <RolesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="permissions"
              element={
                <ProtectedRoute requiredPermissions={["identity:permissions:view"]}>
                  <PermissionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/subscriptions"
              element={
                <ProtectedRoute requiredPermissions={["subscriptions:manage"]}>
                  <SubscriptionsPage />
                </ProtectedRoute>
              }
            />
            {/* Connections (LLM / Source Control / Work Item / CRM) —
                deliberately NOT under admin/: Source Control/Work Item are
                a flat, per-user-owned model, and LLM's list route has no
                permission gate at all. No real permission key exists yet
                for the page itself either, feature-flagged in navConfig.jsx
                (FEATURE_CONNECTORS_ENABLED) — registering still enforces a
                permission server-side regardless: connections:write_personal
                for Source Control/Work Item, connections:write/write-at-level
                for LLM. */}
            <Route
              path="connectors"
              element={
                <ProtectedRoute>
                  <ConnectorsPage />
                </ProtectedRoute>
              }
            />

            {/* Accounts & Projects — real, backend-integrated
                (account:manage / conductor:engagement:* permissions,
                Conductor engagements). The older duplicate top-level
                /accounts and /projects list routes (AccountTierPage,
                EngagementsPage) were removed — this is the one surface now;
                the detail routes moved here under it accordingly. */}
            <Route
              path="admin/accounts"
              element={
                <ProtectedRoute requiredPermissions={["account:manage"]}>
                  <AccountsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/accounts/:accountId"
              element={
                <ProtectedRoute requiredPermissions={["account:manage"]}>
                  <AccountDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/projects"
              element={
                // Deliberately not permission-gated at the route level —
                // mirrors GET /api/conductor/engagements' own design: a
                // project-scoped-only user (no org-wide conductor:engagement:view/manage)
                // must still be able to reach their own projects list.
                <ProtectedRoute>
                  <ProjectsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/projects/:engagementId"
              element={
                <ProtectedRoute>
                  <EngagementDetailPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="accelerators"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["Accelerators"] — no matching permission exists in the new catalog */>
                  <AcceleratorsPage />
                </ProtectedRoute>
              }
            />
            {/* ---------- RAG / KB MODULE ---------- */}
            {/* Primary paths (recommended) */}
            <Route
              path="rag/kb"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["RAG"] — no rag:* permission exists in architecture_scaffold's catalog yet */>
                  <RagCollectionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="rag/kb/:id"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["RAG"] — no rag:* permission exists in architecture_scaffold's catalog yet */>
                  <RagCollectionDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="rag/search"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["RAG"] — no rag:* permission exists in architecture_scaffold's catalog yet */>
                  <RagSearchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="rag/analytics"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["RAG"] — no rag:* permission exists in architecture_scaffold's catalog yet */>
                  <RagAnalyticsPage />
                </ProtectedRoute>
              }
            />

            {/* Legacy aliases (keep for now so nothing breaks) */}
            <Route
              path="rag-collections"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["RAG"] — no rag:* permission exists in architecture_scaffold's catalog yet */>
                  <RagCollectionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="rag-collections/:id"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["RAG"] — no rag:* permission exists in architecture_scaffold's catalog yet */>
                  <RagCollectionDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="rag-search"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["RAG"] — no rag:* permission exists in architecture_scaffold's catalog yet */>
                  <RagSearchPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="rag-analytics"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["RAG"] — no rag:* permission exists in architecture_scaffold's catalog yet */>
                  <RagAnalyticsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="rag-jobs"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["RAG"] — no rag:* permission exists in architecture_scaffold's catalog yet */>
                  <RagJobsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rag/kb/:id/scripts"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["RAG"] — no rag:* permission exists in architecture_scaffold's catalog yet */>
                  <RagClientScriptsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/datasources"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["Dashboard"] — no matching permission exists in the new catalog */>
                  <DataSourcesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/datasets"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["Dashboard"] — no matching permission exists in the new catalog */>
                  <DatasetsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/csv-datasets"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["Dashboard"] — no matching permission exists in the new catalog */>
                  <CSVDatasetsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/csv-upload"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["Dashboard"] — no matching permission exists in the new catalog */>
                  <CSVDatasetUpload />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboards"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["Dashboard"] — no matching permission exists in the new catalog */>
                  <DashboardEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mydashboards"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["Dashboard"] — no matching permission exists in the new catalog */>
                  <MyDashboardsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rules-config"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["sow:view"] -- sow:view gates SOW
                analysis results, a different resource; the rule catalog is gated by sow:rules:*
                instead, checked in-page (RulesConfigPage's own canViewPage) since it needs
                hasPermissionAnywhere (global/org-wide/any account), not hasPermission's
                currently-selected-scope-only check */>
                  <RulesConfigPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports"
              element={
                <ProtectedRoute requiredPermissions={["sow:view"]} /* was ["SOW"] */>
                  <ReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/:id"
              element={
                <ProtectedRoute requiredPermissions={["sow:view"]} /* was ["SOW"] */>
                  <ReportDetailPage />
                </ProtectedRoute>
              }
            />

            {/* Module-level gate first (subscription + coarse sow:analyze/
                sow:view floor, matchAnywhere since these are commonly
                account-scoped grants — see navConfig.jsx's own "planning"
                comment) -- per-route ProtectedRoute below stays for its
                own permission string, unchanged. */}
            <Route
              element={
                <ModuleRouteGuard
                  moduleKey="sow_analysis"
                  permissions={["sow:analyze", "sow:view"]}
                  matchAnywhere
                />
              }
            >
              <Route
                path="/sowdraft"
                element={
                  <ProtectedRoute requiredPermissions={["sow:view"]} /* was ["SOW"] */>
                    <SowDraftPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sowtemplate"
                element={
                  <ProtectedRoute requiredPermissions={["sow:view"]} /* was ["SOW"] */>
                    <SowTemplatePage />
                  </ProtectedRoute>
                }
              />
              {/* Combines what used to be two pages (SOW History Analysis +
                  SOW Analysis Viewer) -- history from every source (SFDC +
                  manual uploads) plus the "Analyze SOWs" trigger, in one. */}
              <Route
                path="/sow-analysis"
                element={
                  <ProtectedRoute requiredPermissions={["sow:view"]} /* was ["SOW"] */>
                    <SowAnalysisPage />
                  </ProtectedRoute>
                }
              />

              {/* SOW reporting. Wrapped in an ErrorBoundary (the app's
                  first — see shared/ui/ErrorBoundary.jsx) because a chart
                  library throwing during render would otherwise blank the
                  whole shell, and in Suspense because both pages are
                  lazy-loaded. */}
              <Route
                path="/reports/sow-usage"
                element={
                  <ProtectedRoute requiredPermissions={["sow:reports:view"]}>
                    <ErrorBoundary title="SOW Usage Report unavailable">
                      <Suspense fallback={<Spinner />}>
                        <SowUsageReportPage />
                      </Suspense>
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/sow-log"
                element={
                  <ProtectedRoute requiredPermissions={["sow:reports:view"]}>
                    <ErrorBoundary title="SOW Log Report unavailable">
                      <Suspense fallback={<Spinner />}>
                        <SowLogReportPage />
                      </Suspense>
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* IDP — Audit & Compliance */}
            <Route
              element={
                <ModuleRouteGuard
                  moduleKey="compliance"
                  permissions={["compliance:view", "compliance:analyze"]}
                />
              }
            >
              <Route path="/compliance" element={<CompliancePage />} />
              {/* :tab makes each module tab (smp/raid/tvr/obligation/stm/rtm/history) a
                  real, linkable URL instead of only internal React state -- CompliancePage
                  reads it via useParams() and falls back to Home when absent/unrecognized. */}
              <Route path="/compliance/:tab" element={<CompliancePage />} />
            </Route>

            <Route
              element={
                <ModuleRouteGuard moduleKey="adai" permissions={["adai:read", "adai:generate"]} />
              }
            >
              <Route
                path="development/adai"
                element={
                  <ProtectedRoute requiredPermissions={["adai:read"]} /* was ["ADAI"] — ADAI is real and mounted */>
                    <AdaiPage />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route
              path="development/reviewgen"
              element={
                <ProtectedRoute requiredPermissions={["reviewgen:read"]}>
                  <ReviewGenPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/mcp"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["MCP"] — no matching permission exists in the new catalog */>
                  <MCPChat />
                </ProtectedRoute>
              }
            />

            <Route
              path="itnew-request"
              element={
                <ProtectedRoute requiredPermissions={["IT_REQUEST_CREATE"]}>
                  <ITNewRequest />
                </ProtectedRoute>
              }
            />

            <Route
              path="myitrequests"
              element={
                <ProtectedRoute requiredPermissions={["IT_REQUEST_CREATE"]}>
                  <ITMyRequests />
                </ProtectedRoute>
              }
            />

            <Route
              path="itapprovals"
              element={
                <ProtectedRoute requiredPermissions={["IT_REQUEST_APPROVE"]}>
                  <ITApprovals />
                </ProtectedRoute>
              }
            />

            <Route
              path="itreq/:id"
              element={
                <ProtectedRoute
                  requiredPermissions={[
                    "IT_REQUEST_CREATE",
                    "IT_REQUEST_APPROVE",
                    "IT_REQUEST_ADMIN",
                  ]}
                >
                  <ITRequestDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="itchatbot"
              element={
                <ProtectedRoute requiredPermissions={["IT_REQUEST_CREATE"]}>
                  <ITChatbot />
                </ProtectedRoute>
              }
            />

            {/* ---------------- CODEBASE ANALYSIS (NEW) ---------------- */}
            <Route
              element={
                <ModuleRouteGuard
                  moduleKey="codebase"
                  permissions={["codebase:manage", "codebase:scan"]}
                />
              }
            >
              <Route
                path="codebase"
                element={
                  <ProtectedRoute requiredPermissions={["codebase:manage"]} /* was ["CODEBASE"] */>
                    <RepoListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="codebase/repos/:repoId"
                element={
                  <ProtectedRoute requiredPermissions={["codebase:manage"]} /* was ["CODEBASE"] */>
                    <RepoOverviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="scoringrules"
                element={
                  <ProtectedRoute requiredPermissions={["codebase:manage"]} /* was ["CODEBASE"] */>
                    <ScoringRulesPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* ── AI Xenon ─────────────────────────────────────────── */}
            <Route
              path="testing/xenon"
              element={
                <ProtectedRoute requiredPermissions={[]} /* was ["XenonAI"] — no matching permission exists in the new catalog */>
                  <XenonAiPage />
                </ProtectedRoute>
              }
            />

            {/* ── Orion GoLive ─────────────────────────────────────────── */}
            <Route
              element={
                <ModuleRouteGuard
                  moduleKey="golive"
                  permissions={["golive:view", "golive:approve", "golive:execute"]}
                />
              }
            >
              <Route path="golive" element={<GoLiveLayout />}>
                <Route
                  index
                  element={
                    <ProtectedRoute requiredPermissions={["golive:view"]}>
                      <GoLiveHomePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="new"
                  element={
                    <ProtectedRoute requiredPermissions={["golive:manage"]}>
                      <NewDeploymentPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="history"
                  element={
                    <ProtectedRoute requiredPermissions={["golive:view"]}>
                      <RunListPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":runId/*"
                  element={
                    <ProtectedRoute requiredPermissions={["golive:view"]}>
                      <RunDetailPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
            </Route>

            {/* ── Requirements & Design Studio (redesign) ──────────────── */}
            {/* No RdsFontReset here on purpose — the new studio is built on
                shared/ui only and carries no module CSS or font shim (plan
                D-4). rds_ui/ below stays reachable until Phase 5 retires it. */}
            <Route
              element={
                <ModuleRouteGuard moduleKey="rds" permissions={["rds:generate", "rds:view"]} />
              }
            >
              <Route path="/studio" element={<StudioHome />} />
              <Route path="/studio/:projectId" element={<StudioProjectLayout />}>
                <Route index element={<Navigate to="sources" replace />} />
                <Route path="sources" element={<SourcesStage />} />
                <Route path="requirements" element={<DocumentStagePage stage="requirements" />} />
                <Route path="design" element={<DocumentStagePage stage="design" />} />
                <Route path="technical" element={<DocumentStagePage stage="technical" />} />
              </Route>
            </Route>

            {/* ── Requirements & Design Studio (legacy — Phase 5 deletes this) ── */}
            <Route element={<RdsFontReset />}>
              <Route
                path="/requirement-studio"
                element={<RdsRequirementStudio />}
              />
              <Route path="/design-studio" element={<RdsDesignStudio />} />
              <Route
                path="/design-studio/projects/:projectId"
                element={<RdsDesignProjectDetail />}
              />
              <Route
                path="/projects/:projectId/sow/:sowId"
                element={<RdsSOWDetail />}
              />
              <Route
                path="/projects/:projectId/generations/:runId"
                element={<RdsGenerationProgress />}
              />
              <Route
                path="/projects/:projectId/generations/:runId/results"
                element={<RdsGenerationResults />}
              />
              <Route
                path="/projects/:projectId/prompts"
                element={<RdsPromptEditor />}
              />
              {/* FAQs */}
              <Route path="faq" element={<Faq/>}/> 
            </Route>
          </Route>

          {/* Redirect old path to new rds_ui path */}
          <Route
            path="/requirements-studio"
            element={<Navigate to="/requirement-studio" replace />}
          />
          <Route
            path="/requirements-studio/*"
            element={<Navigate to="/requirement-studio" replace />}
          />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>   
    </BrowserRouter>
  );
}
